import { Component, EventEmitter, inject, Input, LOCALE_ID, OnChanges, Output, SimpleChanges } from '@angular/core'

import { MenuItem } from 'primeng/api'
import { Button } from 'primeng/button'
import { DialogService } from 'primeng/dynamicdialog'
import { Menu } from 'primeng/menu'

import { forEach } from 'lodash-es'

import { WorkflowState, WorkflowTransition } from '@app/interfaces/workflows'
import { WorkflowApi } from '@app/apis/workflows.api'
import { SignalService } from '@app/services/signal.service'
import { UtilitiesService } from '@app/services/utilities.service'

import { WorkflowVisualisationComponent } from './workflow-visualisation.component'

@Component({
  selector: 'app-workflow',
  templateUrl: 'workflow.component.html',
  imports: [
    Button,
    Menu,
  ],
})
export class WorkflowComponent<T, U, V> implements OnChanges {
  public readonly utilitiesService = inject(UtilitiesService)
  public readonly dialogService = inject(DialogService)
  public readonly signalService = inject(SignalService)
  public readonly locale = inject(LOCALE_ID)

  // block transitions while the disabled flag is true
  @Input() disabled: boolean = false
  // API
  @Input() api!: WorkflowApi<T, V>
  // current state to display
  @Input() state!: WorkflowState<U>
  // list of possible transitions for the current user / object
  @Input() transitions: WorkflowTransition<U, V>[] = []
  // code / id / uuid for the instance being transitioned, used by the api for the 'lookup field', along with the `baseUrl`
  @Input() code: string | number = ''
  // workflow name (=workflow code)
  @Input() workflowName: string = ''
  // optional detail when access to arbitrary object properties are required for e.g. custom transitions / triggers
  @Input() detail?: T

  // event emitter when the workflow changes, called with the new object detail as an argument
  @Output() workflowUpdated = new EventEmitter<T>()
  // An optional function which can be used to intercept the transition and perform exotic stuff such as capture additional
  //   data, display a message, etc.
  // If this function returns `false` the transition will not be called.
  // Note to have access to the 'parent' scope in the interceptor function use this syntax: (e.g. sec21)
  // e.g.: [interceptorFunction]="workflowInterceptorFunction.bind(this)"
  @Input() interceptorFunction: CallableFunction = (_transitionName: string, _code: string | number): boolean => { return true }

  // items for the transitions menu
  public transitionItems: MenuItem[] = []

  // The name of the field to target for translatable objects.
  // This will be overridden if we detect the `fr` locale
  public labelField = 'label_en'

  constructor () {
    // target french label fields if we're in french-mode
    if (this.locale === 'fr') {
      this.labelField = 'label_fr'
    }
  }

  /**
   * Changes made to the `transitions` input must trigger the rebuilding of the `transitionItems` menu items.
   * Here we detect when the parent component changes our transitions and rebuild the menu items accordingly.
   */
  ngOnChanges (changes: SimpleChanges<WorkflowComponent<T, U, V>>) {
    if (changes.transitions?.previousValue !== changes.transitions?.currentValue) {
      this.rebuildData()
    }
  }

  /**
   * Rebuild the state and transitionItems list from the current data
   */
  rebuildData () {
    this.transitionItems = []

    forEach(this.transitions, (transition: WorkflowTransition<U, V>) => {
      // generate the locale-aware transition label
      let theLabel = transition.label_en
      if (this.locale === 'fr') theLabel = transition.label_fr

      this.transitionItems.push({
        label: theLabel,
        icon: 'fa fa-solid fa-play',
        command: () => {
          this.playTransition(transition.name)
        },
      })
    })
    this.transitionItems.push({ separator: true })
    this.transitionItems.push({
      label: $localize`:@@workflow-visualise-workflow:Visualise workflow`,
      icon: 'fa fa-solid fa-eye',
      command: () => {
        this.visualiseWorkflow()
      },
    })
  }

  /**
   * call the transition play mixin endpoint (WorkflowViewSetMixin) for this object / transition
   */
  private playTransition (transitionName: V) {
    // If we were passed an interceptor function then call it now, and determine if we should continue with the workflow transition.
    const verdict = this.interceptorFunction(transitionName, this.code)
    if (!verdict) return

    // There was either no interceptor function or it returned `true`, lets advance the workflow
    this.api.play(this.code, transitionName).subscribe((response: T) => {
      // call was success, emit new detail which will cascade back down to this component
      this.workflowUpdated.emit(response)
    })
  }

  /**
   * open a modal displaying the workflow as an image
   */
  visualiseWorkflow () {
    this.dialogService.open(WorkflowVisualisationComponent, {
      header: $localize`:@@workflow-visualise-workflow:Visualise workflow`,
      width: '80vw',
      dismissableMask: true,
      data: {
        workflowName: this.workflowName,
        api: this.api,
      },
    })
  }

  toggleMenu (menu: Menu, $event: any) {
    if (this.disabled) {
      menu.hide()
    } else {
      menu.toggle($event)
    }
  }
}
